/**
 * MMO combat integration (2026-08-24) — the numbered action bar, resource
 * bar and cast bar from the user's reference HUD.
 *
 * Built the way every other panel in `engine/ui/` is: plain DOM plus Tailwind
 * utility classes, driven by the server's per-player `ability.state` message
 * (the same pattern `LootPanel` uses for private state). No canvas work, so
 * it composites over the 3D scene without touching the renderer.
 *
 * Mobile-first per CLAUDE.md: every slot is a real button at 44x44 CSS px
 * minimum, driven by `pointerdown` rather than `mousedown` so a tap works,
 * and the row wraps rather than overflowing at 320px.
 */

import { RESOURCE_TINT as HUD_RESOURCE_TINT } from './hud-theme';
import {
  abilityCssColor, abilityIconUrl, abilityVisual, consumableIconUrl, type AbilityVisual,
} from '../config/ability-vfx';
import { t, tc, N_, NC_, getIntlLocale } from '../i18n/i18n';

/**
 * The slot label for each satchel charge — its own key, not the last word of
 * the translated name (i18n, 2026-10-16): "Сальная мазь" ends in the right
 * word by luck, "Baume de suif" does not. Unknown ids show the full name.
 */
const CONSUMABLE_SHORT: Record<string, string> = {
  salve: NC_('short label', 'Salve'),
  tonic: NC_('short label', 'Tonic'),
  whetstone: NC_('short label', 'Whetstone'),
  draught: NC_('short label', 'Draught'),
  poultice: NC_('short label', 'Poultice'),
};

/**
 * Font size for a consumable slot's one-line label. The slot leaves ~48px of
 * text width; at 10px semibold that holds about seven characters, so longer
 * words step down rather than wrap. Floor of 8px — below that it is unreadable
 * and the ellipsis takes over.
 */
export function consumableLabelSizePx(label: string): number {
  const n = [...label].length;
  if (n <= 7) return 10;
  if (n <= 9) return 9;
  return 8;
}

export interface AbilitySlotView {
  id: string;
  slot: number;
  name: string;
  cost: number;
  castTimeMs: number;
  /** Server timestamp this ability is usable again. */
  readyAt: number;
  /**
   * Targeting and reach (2026-10-16). Optional so an older server, or a test
   * fixture written against the old payload, still renders a bar — it just
   * gets no range tint and a thinner card.
   */
  targeting?: AbilityTargeting;
  rangeM?: number;
  radiusM?: number;
  cooldownMs?: number;
  /** English, translated where drawn — `served-copy` carries it. */
  description?: string;
}

export type AbilityTargeting = 'SELF' | 'ALLY' | 'ENEMY' | 'AOE_ENEMY';

/**
 * What the frame loop knows about the current (or would-be) enemy target, for
 * the range tint. `distanceM` null means there is no enemy to act on at all.
 */
export interface TargetContext {
  distanceM: number | null;
}

/** The bars a cost is paid from, named on the card. */
const RESOURCE_NAME: Record<string, string> = { MANA: N_('Mana'), RAGE: N_('Rage'), ENERGY: N_('Energy') };

/** Held this long on a touch screen, a slot shows its card instead of firing. */
export const LONG_PRESS_MS = 380;
/** A mouse resting on a slot this long opens its card. Short: it is the only way to learn the bar. */
const HOVER_DELAY_MS = 220;

/**
 * The lines on an ability's card, as text — pure so the test can read it.
 *
 * Replaces the `title` tooltip (2026-10-16), which a phone never shows, a
 * mouse shows after a second and a half, and which said only cost and cast.
 * A player asked for hints; the card says what the ability is for, what it
 * needs as a target, and how far it reaches.
 */
export function abilityCardLines(ability: AbilitySlotView, resourceKind: string): {
  name: string; description: string; target: string; facts: string[];
} {
  const locale = getIntlLocale();
  const seconds = (ms: number): string => (ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 });
  let target = '';
  switch (ability.targeting) {
    case 'ENEMY': target = t('Target: an enemy'); break;
    case 'ALLY': target = t('Target: a squadmate, or the most hurt one nearby'); break;
    case 'SELF': target = t('Target: yourself'); break;
    case 'AOE_ENEMY': target = t('Target: every enemy within {r}m of you', { r: ability.radiusM ?? 0 }); break;
    default: target = '';
  }
  const facts: string[] = [];
  if (ability.rangeM !== undefined && (ability.targeting === 'ENEMY' || ability.targeting === 'ALLY')) {
    facts.push(t('Range {m}m', { m: ability.rangeM }));
  }
  if (ability.cost > 0) {
    const named = RESOURCE_NAME[resourceKind];
    facts.push(t('Costs {cost} {resource}', { cost: ability.cost, resource: named ? t(named) : resourceKind }));
  }
  facts.push(ability.castTimeMs > 0 ? t('{s}s cast', { s: seconds(ability.castTimeMs) }) : t('instant'));
  if (ability.cooldownMs && ability.cooldownMs > 0) facts.push(t('{s}s cooldown', { s: seconds(ability.cooldownMs) }));
  return {
    name: t(ability.name),
    description: ability.description ? t(ability.description) : '',
    target,
    facts,
  };
}

/** Whether an ability that needs an enemy is out of reach of the one we have — the red tint. */
export function enemySlotOutOfReach(ability: AbilitySlotView, ctx: TargetContext | null): boolean {
  if (ability.targeting !== 'ENEMY' || !ctx) return false;
  if (ctx.distanceM === null) return true;
  return ability.rangeM !== undefined && ctx.distanceM > ability.rangeM;
}

/**
 * Quick-use consumables (2026-08-25) — one satchel slot, as the server's
 * `ability.state` reports it.
 *
 * Sent for every consumable the class can use, including at zero charges: an
 * emptied slot has to keep rendering so a player can see they drank their last
 * Salve. That is the difference from the hub's pre-run preview, which omits
 * what you do not own.
 */
export interface ConsumableSlotView {
  id: string;
  slot: number;
  name: string;
  effect: string;
  charges: number;
  maxCharges: number;
  channelMs: number;
  readyAt: number;
}

export interface AbilityStateView {
  resource: number;
  resourceMax: number;
  resourceKind: string;
  gcdReadyAt: number;
  /**
   * The in-flight cast's window, sent with the rest of the private ability
   * state rather than read off synced schema — see the note in the server's
   * `room-state.ts` on why none of this lives on `PlayerState`.
   */
  castingAbilityId?: string;
  castStartedAt?: number;
  castEndsAt?: number;
  abilities: AbilitySlotView[];
  /**
   * Optional so a server that predates consumables (or a test fixture written
   * against the old payload) still renders an action bar rather than throwing
   * on a missing array.
   */
  consumables?: ConsumableSlotView[];
}

/**
 * Re-exported from the HUD theme rather than declared again here. It was
 * declared twice with different values (sky-500/red-600/yellow-400 against the
 * player frame's own set), so the same rage bar was two different reds
 * depending on which panel you looked at.
 */
const RESOURCE_TINT = HUD_RESOURCE_TINT;

/**
 * One ability's glyph as an inline SVG, stroked in its own colour.
 *
 * Was the face of the slot; now its **fallback**. Painted art (2026-08-26)
 * took the front, and the glyph stays behind it for the one case art has that
 * path data does not: a failed fetch. It costs a few hundred bytes, needs no
 * network, and keeps a 404'd bar readable instead of blank. Same shape of
 * markup `core/auth` already uses for its password-visibility icons.
 */
function abilityGlyphElement(visual: AbilityVisual): SVGSVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke-width', '1.6');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'absolute top-1.5 left-1/2 -translate-x-1/2 w-7 h-7 pointer-events-none');
  svg.style.stroke = abilityCssColor(visual.colorHex);
  // A faint wash of the same colour behind the strokes, so a glyph reads as
  // an icon rather than as scratches on a grey square.
  svg.style.filter = `drop-shadow(0 0 3px ${abilityCssColor(visual.colorHex)}66)`;
  const path = document.createElementNS(NS, 'path');
  path.setAttribute('d', visual.glyph);
  svg.appendChild(path);
  return svg;
}

/**
 * The painted plate that fills a slot, with the glyph behind it as a net.
 *
 * `object-cover` on a square icon in a square slot, so nothing is cropped —
 * the art is generated at 1:1 for exactly this. The image is *removed* on
 * error rather than left broken, which is what un-hides the glyph: a slot has
 * to keep saying which ability it is even when its art never arrived.
 */
function abilityArtElement(url: string, onFailure: () => void): HTMLImageElement {
  const img = document.createElement('img');
  img.src = url;
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  img.draggable = false;
  img.decoding = 'async';
  img.className = 'absolute inset-0 w-full h-full object-cover pointer-events-none select-none';
  // Restyled 2026-09-01: the plates used to be dark paintings that went muddy
  // at 52px and needed a saturation and brightness lift to survive. The new
  // cartoon plates are already bright and saturated, so that same lift blew
  // out the highlights. A touch of contrast is all that is left.
  img.style.filter = 'contrast(1.04)';
  img.addEventListener('error', () => { img.remove(); onFailure(); });
  return img;
}

/**
 * A scrim over the bottom of the plate.
 *
 * The art is a painted scene and the ability's name sits on top of it, so
 * "Sinister Strike" would otherwise land letters on a lit green blade and lose
 * half of them. Only the bottom is darkened, so the subject of the icon still
 * reads — which is the half of the slot a player learns the ability by.
 */
function slotScrimElement(): HTMLDivElement {
  const scrim = document.createElement('div');
  // Warm brown rather than black, and shallower than it was: on the old dark
  // plates a near-opaque black wash was invisible, but over bright cartoon art
  // it reads as a stain. This is the least that still carries the name.
  scrim.className = 'absolute inset-x-0 bottom-0 h-1/2 pointer-events-none '
    + 'bg-[linear-gradient(0deg,rgba(28,16,8,0.92)_0%,rgba(28,16,8,0.6)_45%,rgba(28,16,8,0)_100%)]';
  return scrim;
}

/**
 * The ability's colour, as an inner edge on its slot.
 *
 * The colour identity is the whole point of `config/ability-vfx.ts` — the mark
 * on the bar and the bloom in the room are the same hue — and painted art
 * cannot carry it, because a plate is many colours. So the hue moves to the
 * frame, where it survives the art.
 *
 * Its own element rather than an inline `box-shadow` on the button: Tailwind's
 * `ring-*` utilities are box-shadows too, and `refreshCooldowns` toggles one
 * of them to mark a slot ready. Writing the shadow inline would silently win
 * that fight and the ready highlight would stop appearing.
 */
function slotEdgeElement(colorHex: number): HTMLDivElement {
  const edge = document.createElement('div');
  edge.className = 'absolute inset-0 rounded-[0.55rem] pointer-events-none';
  const css = abilityCssColor(colorHex);
  edge.style.boxShadow = `inset 0 0 0 1px ${css}b0, inset 0 0 10px ${css}45`;
  return edge;
}

export class ActionBar {
  private readonly root: HTMLDivElement;
  private readonly resourceFill: HTMLDivElement;
  private readonly resourceLabel: HTMLDivElement;
  private readonly castWrap: HTMLDivElement;
  private readonly castFill: HTMLDivElement;
  private readonly slotsRow: HTMLDivElement;
  private readonly slotEls = new Map<number, { button: HTMLButtonElement; cooldown: HTMLDivElement; charges?: HTMLSpanElement; reach?: HTMLDivElement }>();
  private state: AbilityStateView | null = null;
  private targetContext: TargetContext | null = null;
  /** The one ability card, shared by every slot — see `abilityCardLines`. */
  private readonly card: HTMLDivElement;
  private cardTimer: ReturnType<typeof setTimeout> | null = null;
  private cardSlot: number | null = null;

  /** Set by the caller; fires with the 1-based slot number. */
  onActivate: (slot: number) => void = () => {};

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.dataset.tutorial = 'action-bar';
    this.root.dataset.hud = 'action-bar';
    this.root.className = 'fixed bottom-0 left-1/2 -translate-x-1/2 z-[1000] flex flex-col items-center gap-1 '
      + 'pb-[max(0.5rem,env(safe-area-inset-bottom))] px-2 w-full max-w-[36rem] pointer-events-none';

    // The cast bar sits above the resource bar: it is the most time-critical
    // readout on screen, so it goes closest to where the eye already is.
    this.castWrap = document.createElement('div');
    this.castWrap.className = 'meter w-40 h-2.5 hidden';
    this.castFill = document.createElement('div');
    this.castFill.className = 'meter-fill bg-[#f5cf3f]';
    this.castWrap.appendChild(this.castFill);

    const resourceWrap = document.createElement('div');
    resourceWrap.className = 'meter w-[13rem] max-w-[60vw] h-2';
    this.resourceFill = document.createElement('div');
    this.resourceFill.className = `meter-fill ${RESOURCE_TINT.MANA}`;
    this.resourceLabel = document.createElement('div');
    this.resourceLabel.className = 'absolute inset-0 text-[10px] leading-[0.625rem] text-center tracking-wide '
      + 'text-[#ffeccc] [text-shadow:0_1px_0_#241309]';
    resourceWrap.append(this.resourceFill, this.resourceLabel);

    this.slotsRow = document.createElement('div');
    this.slotsRow.className = 'flex flex-wrap justify-center gap-1.5 pointer-events-auto';

    // The ability card. Fixed and pointer-events-none: it must never sit
    // between a thumb and the slot it describes, and it follows the slot it
    // was opened from rather than a corner.
    this.card = document.createElement('div');
    this.card.dataset.hud = 'ability-card';
    this.card.setAttribute('role', 'tooltip');
    this.card.className = 'fixed z-[1250] pointer-events-none hidden w-[15rem] max-w-[calc(100vw-1.5rem)] '
      + 'rounded-md px-3 py-2 text-left bg-[#1b140d]/95 border border-[#8a6531] '
      + 'shadow-[0_8px_24px_rgba(0,0,0,0.6)] text-[#ffeccc]';

    this.root.append(this.castWrap, resourceWrap, this.slotsRow);
    container.append(this.root, this.card);
  }

  /**
   * The frame loop's view of the target, for the range tint (2026-10-16). An
   * enemy ability with nothing in reach is drawn red-edged before it is
   * pressed, rather than refused after.
   */
  setTargetContext(ctx: TargetContext | null): void {
    this.targetContext = ctx;
  }

  private showCard(slot: number, anchor: HTMLElement): void {
    const ability = this.state?.abilities.find((a) => a.slot === slot);
    if (!ability || !this.state) return;
    const lines = abilityCardLines(ability, this.state.resourceKind);
    const title = document.createElement('div');
    title.className = 'text-[13px] font-bold font-heading';
    title.textContent = lines.name;
    const parts: HTMLElement[] = [title];
    if (lines.description) {
      const desc = document.createElement('div');
      desc.className = 'text-[12px] leading-snug mt-0.5';
      desc.textContent = lines.description;
      parts.push(desc);
    }
    if (lines.target) {
      const target = document.createElement('div');
      target.className = 'text-[11px] mt-1 text-amber-200';
      target.textContent = lines.target;
      parts.push(target);
    }
    const facts = document.createElement('div');
    facts.className = 'text-[11px] mt-0.5 opacity-80';
    facts.textContent = lines.facts.join(' · ');
    parts.push(facts);
    this.card.replaceChildren(...parts);
    this.card.classList.remove('hidden');
    this.cardSlot = slot;

    // Above the slot, clamped to the viewport's 0.75rem gutter.
    const rect = anchor.getBoundingClientRect();
    const width = this.card.offsetWidth || 240;
    const left = Math.max(12, Math.min(window.innerWidth - width - 12, rect.left + rect.width / 2 - width / 2));
    this.card.style.left = `${left}px`;
    this.card.style.bottom = `${Math.max(12, window.innerHeight - rect.top + 8)}px`;
  }

  private hideCard(): void {
    if (this.cardTimer) { clearTimeout(this.cardTimer); this.cardTimer = null; }
    this.card.classList.add('hidden');
    this.cardSlot = null;
  }

  /** Whether the card is open, and for which slot — for tests and the tutorial. */
  get openCardSlot(): number | null {
    return this.cardSlot;
  }

  /**
   * Wires one ability slot: a mouse hover opens the card; a tap fires; on a
   * touch screen, holding opens the card *instead* of firing, so reading an
   * ability never spends it. A mouse or a pen still fires on pointerdown —
   * the latency a GCD-paced rotation wants; a finger fires on release, which
   * is the only way to tell a tap from a hold.
   */
  private wireAbilitySlot(button: HTMLButtonElement, slot: number): void {
    let pressStartedAt = 0;
    let touchPress = false;
    let longPressed = false;
    button.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      touchPress = (e as PointerEvent).pointerType === 'touch';
      if (!touchPress) { this.hideCard(); this.onActivate(slot); return; }
      pressStartedAt = performance.now();
      longPressed = false;
      if (this.cardTimer) clearTimeout(this.cardTimer);
      this.cardTimer = setTimeout(() => { longPressed = true; this.showCard(slot, button); }, LONG_PRESS_MS);
    });
    const endTouch = (fire: boolean): void => {
      if (!touchPress) return;
      touchPress = false;
      const wasLong = longPressed || performance.now() - pressStartedAt >= LONG_PRESS_MS;
      this.hideCard();
      if (fire && !wasLong) this.onActivate(slot);
    };
    button.addEventListener('pointerup', (e) => { e.preventDefault(); endTouch(true); });
    button.addEventListener('pointercancel', () => endTouch(false));
    button.addEventListener('pointerenter', (e) => {
      if ((e as PointerEvent).pointerType !== 'mouse') return;
      if (this.cardTimer) clearTimeout(this.cardTimer);
      this.cardTimer = setTimeout(() => this.showCard(slot, button), HOVER_DELAY_MS);
    });
    button.addEventListener('pointerleave', (e) => {
      if ((e as PointerEvent).pointerType === 'mouse') this.hideCard();
    });
    // Keyboard focus reads the card too — keyboard only, or every mouse click
    // would leave a card standing until the next click elsewhere.
    button.addEventListener('focus', () => {
      let keyboard = false;
      try { keyboard = button.matches(':focus-visible'); } catch { keyboard = false; }
      if (keyboard) this.showCard(slot, button);
    });
    button.addEventListener('blur', () => this.hideCard());
    button.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  update(state: AbilityStateView, serverNow: number): void {
    const changedShape = this.state === null
      || this.state.abilities.length !== state.abilities.length
      || this.state.abilities.some((a, i) => a.id !== state.abilities[i].id)
      // Charge counts change constantly and must NOT rebuild the row — only
      // the set of slots does. Comparing ids rather than the whole array is
      // what keeps drinking a potion from re-creating every button (and
      // dropping the pointer capture of whichever one you are pressing).
      || (this.state?.consumables ?? []).length !== (state.consumables ?? []).length
      || (this.state?.consumables ?? []).some((c, i) => c.id !== (state.consumables ?? [])[i]?.id);
    this.state = state;
    if (changedShape) this.rebuildSlots(state);

    // HUD slice (2026-08-24) — the resource bar moved to the player frame
    // (`combat-hud.ts`), where it sits with HP and stamina instead of on the
    // far side of the screen from them. It stays here too, thin and unlabelled,
    // because "can I afford the button I am about to press" is a question about
    // the button: the slots dim when unaffordable, and this shows how close the
    // next one is.
    // Clamped for the same reason `combat-hud.ts`'s `fillWidth` is — a fill
    // wider than its track escapes the bar.
    const pct = state.resourceMax > 0
      ? Math.min(100, Math.max(0, (state.resource / state.resourceMax) * 100))
      : 0;
    this.resourceFill.style.width = `${pct}%`;
    this.resourceFill.className = `h-full rounded-full ${RESOURCE_TINT[state.resourceKind] ?? RESOURCE_TINT.MANA}`;
    this.resourceLabel.textContent = '';
    this.refreshCooldowns(serverNow);
  }

  /**
   * Called every frame with the estimated server clock, so cooldown shading
   * animates smoothly instead of stepping once per message. Writes only to
   * elements that changed.
   */
  refreshCooldowns(serverNow: number): void {
    const state = this.state;
    if (!state) return;
    for (const ability of state.abilities) {
      const el = this.slotEls.get(ability.slot);
      if (!el) continue;
      const ownRemaining = Math.max(0, ability.readyAt - serverNow);
      const gcdRemaining = Math.max(0, state.gcdReadyAt - serverNow);
      const remaining = Math.max(ownRemaining, gcdRemaining);
      const affordable = state.resource >= ability.cost;

      el.cooldown.style.height = remaining > 0 ? '100%' : '0%';
      el.cooldown.textContent = ownRemaining > 0 ? Math.ceil(ownRemaining / 1000).toString() : '';
      // Unaffordable reads differently from on-cooldown on purpose: they are
      // different problems and the player must tell them apart at a glance.
      // Drained, not transparent. `opacity-40` was right for a line glyph on a
      // dark square, but a *painted* slot at 40% lets the dungeon floor shine
      // through it and the whole bar reads as bleached — the live capture of
      // the first painted pass showed exactly that. Grey and dark keeps the
      // slot solid and still says "not now" at a glance.
      el.button.classList.toggle('grayscale', !affordable);
      el.button.classList.toggle('brightness-50', !affordable);
      el.button.classList.toggle('ring-gold-bright', remaining === 0 && affordable);
      // Range (2026-10-16): red-edged, not dimmed — "move closer" is a
      // different problem from "wait" or "you cannot afford it".
      el.reach?.classList.toggle('hidden', !enemySlotOutOfReach(ability, this.targetContext));
    }

    // Quick-use consumables — same treatment, with charges standing in for
    // cost. An empty slot dims exactly like an unaffordable ability, because
    // it is the same problem from the player's side: pressing it does nothing.
    for (const consumable of state.consumables ?? []) {
      const el = this.slotEls.get(consumable.slot);
      if (!el) continue;
      const ownRemaining = Math.max(0, consumable.readyAt - serverNow);
      const remaining = Math.max(ownRemaining, Math.max(0, state.gcdReadyAt - serverNow));
      const hasCharges = consumable.charges > 0;

      el.cooldown.style.height = remaining > 0 ? '100%' : '0%';
      el.cooldown.textContent = ownRemaining > 0 ? Math.ceil(ownRemaining / 1000).toString() : '';
      if (el.charges) el.charges.textContent = String(consumable.charges);
      el.button.classList.toggle('grayscale', !hasCharges);
      el.button.classList.toggle('brightness-50', !hasCharges);
      el.button.classList.toggle('ring-gold-bright', remaining === 0 && hasCharges);
    }
  }

  setCast(fraction: number | null): void {
    if (fraction === null) { this.castWrap.classList.add('hidden'); return; }
    this.castWrap.classList.remove('hidden');
    this.castFill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  }

  private rebuildSlots(state: AbilityStateView): void {
    this.hideCard();
    this.slotsRow.replaceChildren();
    this.slotEls.clear();
    for (const ability of state.abilities) {
      const button = document.createElement('button');
      button.type = 'button';
      // 52px, comfortably over CLAUDE.md's 44px tap-target floor (Apple HIG /
      // Material). The old 44px minimum plus a 10px label meant the ability
      // name was the first thing to become unreadable, and an action bar you
      // cannot read the names on is a row of identical grey squares.
      button.className = 'relative w-[3.25rem] h-[3.25rem] rounded-md border border-[#514132] '
        + 'bg-[linear-gradient(180deg,#33291e,#1e170f)] '
        + 'shadow-[inset_0_1px_0_rgba(255,222,176,0.1),0_3px_12px_rgba(0,0,0,0.55)] '
        + 'text-text flex flex-col items-center justify-end pb-1 overflow-hidden select-none touch-none '
        + 'transition-[box-shadow,opacity,transform] duration-100';
      const seconds = (ability.castTimeMs / 1000).toLocaleString(getIntlLocale());
      const timing = ability.castTimeMs > 0 ? t('{s}s cast', { s: seconds }) : t('instant');
      // The hover / long-press card replaced the `title` tooltip (2026-10-16);
      // the short summary stays as the accessible name.
      button.setAttribute('aria-label', ability.cost > 0
        ? t('{ability} — {cost}, {timing}', { ability: t(ability.name), cost: ability.cost, timing })
        : t('{ability} — {timing}', { ability: t(ability.name), timing }));

      // The keybind gets its own badge rather than a faint corner digit: it is
      // what the player is looking for when they look down at the bar.
      const key = document.createElement('span');
      key.className = 'absolute top-0.5 left-1 text-[11px] font-bold text-[#ffeccc] [text-shadow:0_1px_2px_#241309]';
      key.textContent = String(ability.slot);

      // A cast time is a property you must know BEFORE pressing — it is the
      // difference between an ability you can use while being hit and one you
      // cannot — so it is on the face of the slot, not only in the tooltip.
      if (ability.castTimeMs > 0) {
        const cast = document.createElement('span');
        cast.className = 'absolute top-0.5 right-1 text-[9px] text-amber-200/90';
        cast.textContent = t('{s}s', { s: seconds });
        button.appendChild(cast);
      }

      /**
       * Generated art (2026-08-26) — the ability's painted face, its colour on
       * the frame, and the line glyph kept behind as the fallback.
       *
       * The row began as fourteen identical grey squares told apart by 9px of
       * text, then became fourteen line glyphs; this is the third pass and the
       * first one that looks like the rest of the game. The colour still
       * matches what blooms in the room when the ability goes off
       * (`config/ability-vfx.ts` holds both), so the bar teaches the world and
       * the world reinforces the bar — it just carries the hue on the edge of
       * the slot now, because a painted plate cannot carry it.
       *
       * The name stays underneath. An icon alone is a rebus until you have
       * learned it, and this is the screen where it gets learned.
       */
      const visual = abilityVisual(ability.id);
      const glyph = abilityGlyphElement(visual);
      glyph.classList.add('hidden');
      button.append(
        abilityArtElement(abilityIconUrl(visual.icon), () => glyph.classList.remove('hidden')),
        slotScrimElement(),
        slotEdgeElement(visual.colorHex),
        glyph,
      );

      const name = document.createElement('span');
      // `relative` is load-bearing: the plate is absolutely positioned, and a
      // positioned element paints above an in-flow sibling regardless of DOM
      // order. Without this the art covers the label — the first live capture
      // of the painted bar showed six slots with no names on them.
      const label = t(ability.name);
      // Long translated names ("Сокрушительный удар") break at a hyphen
      // instead of being clipped mid-word, and drop a size when they are long.
      name.className = `relative max-w-full px-0.5 text-center ${label.length > 12 ? 'text-[8px]' : 'text-[9px]'} leading-[1.05] font-semibold text-[#ffeccc] `
        + 'break-words hyphens-auto [text-shadow:0_1px_2px_#000]';
      name.textContent = label;

      const cooldown = document.createElement('div');
      // Named, because a slot is no longer one div deep: the plate, the scrim
      // and the coloured edge are divs too, and "the first div in the button"
      // stopped meaning the cooldown shade the moment art landed.
      cooldown.dataset.role = 'cooldown';
      cooldown.className = 'absolute inset-x-0 bottom-0 bg-[#0d0905]/80 text-[13px] font-bold text-[#ffeccc] '
        + 'flex items-center justify-center pointer-events-none';
      cooldown.style.height = '0%';

      // The out-of-reach edge — see `refreshCooldowns`.
      const reach = document.createElement('div');
      reach.dataset.role = 'out-of-range';
      reach.className = 'absolute inset-0 rounded-[0.55rem] pointer-events-none hidden '
        + 'shadow-[inset_0_0_0_2px_rgba(224,72,58,0.95),inset_0_0_14px_rgba(224,72,58,0.55)] bg-[#e0483a]/15';

      button.append(key, name, reach, cooldown);
      // pointerdown, not click, for a mouse: it gives a press the same latency
      // a keyboard gets. A finger fires on release — see `wireAbilitySlot`.
      this.wireAbilitySlot(button, ability.slot);
      this.slotsRow.appendChild(button);
      this.slotEls.set(ability.slot, { button, cooldown, reach });
    }
    this.buildConsumableSlots(state.consumables ?? []);
  }

  /**
   * The satchel slots, drawn in the same row as the abilities.
   *
   * Same row on purpose rather than a second bar: they share the GCD and the
   * numbered keys (`Digit6`/`Digit7`), so splitting them off would imply a
   * separate resource and a separate rhythm that do not exist. The amber ring
   * is what says "item, not ability" — worth distinguishing, because a charge
   * is finite and a cooldown is not.
   */
  private buildConsumableSlots(consumables: readonly ConsumableSlotView[]): void {
    if (consumables.length === 0) return;

    const divider = document.createElement('div');
    divider.className = 'w-px self-stretch my-1 bg-white/20';
    this.slotsRow.appendChild(divider);

    for (const consumable of consumables) {
      const button = document.createElement('button');
      button.type = 'button';
      // Brass-rimmed rather than ink-rimmed: a consumable is a different kind
      // of thing from an ability and the row should say so without a label.
      button.className = 'relative w-[3.25rem] h-[3.25rem] rounded-md border border-[#8a6531] '
        + 'bg-[linear-gradient(180deg,#33291e,#1e170f)] '
        + 'shadow-[inset_0_1px_0_rgba(255,222,176,0.14),0_3px_12px_rgba(0,0,0,0.55)] '
        + 'text-text flex flex-col items-center justify-end pb-1 overflow-hidden select-none '
        + 'transition-[box-shadow,opacity,transform] duration-100';
      button.title = t('{name} — {s}s channel, {charges}/{max} left. Interrupted by moving or being hit.', {
        name: t(consumable.name),
        s: (consumable.channelMs / 1000).toLocaleString(getIntlLocale()),
        charges: consumable.charges,
        max: consumable.maxCharges,
      });

      const key = document.createElement('span');
      key.className = 'absolute top-0.5 left-1 text-[11px] font-bold text-[#ffeccc] [text-shadow:0_1px_2px_#241309]';
      key.textContent = String(consumable.slot);

      /**
       * Charges take the top-right corner — where an ability shows its cast
       * time — because on a 52px slot it is the number that has to survive.
       *
       * It started bottom-right, under the name, and a screenshot showed the
       * count sitting on top of "Salve"/"Whetstone": both are two words that
       * wrap to two lines at 9px, so the badge and the second line fought for
       * the same pixels. The channel length moved to the tooltip in the trade,
       * which costs little — it is 1.5s on every consumable, so on the face it
       * was the same glyph three times, while charges are the only thing here
       * that cannot come back on its own.
       */
      const charges = document.createElement('span');
      charges.className = 'absolute top-0.5 right-1 text-[11px] font-bold text-amber-100 '
        + '[text-shadow:0_1px_2px_#000] pointer-events-none';
      charges.textContent = String(consumable.charges);

      /**
       * The distinctive word, not the full name: "Tallow Salve" wraps to two
       * lines and truncates to mush at this size, while "Salve" is the half a
       * player actually says. The full name stays in the tooltip.
       */
      const name = document.createElement('span');
      // `relative` for the reason given on the ability label: it has to paint
      // over the plate, not under it.
      // One line, never broken mid-word (QA 10.10.2026: "Whetston / e").
      // A long word steps the size down instead, and anything still too wide
      // ends in an ellipsis; the full name is in the tooltip either way.
      const short = CONSUMABLE_SHORT[consumable.id];
      const label = short ? tc('short label', short) : t(consumable.name);
      name.className = 'relative block max-w-full px-0.5 text-center leading-[1.05] font-semibold text-[#ffeccc] '
        + 'whitespace-nowrap overflow-hidden text-ellipsis tracking-[-0.01em] [text-shadow:0_1px_2px_#000]';
      name.style.fontSize = consumableLabelSizePx(label) + 'px';
      name.textContent = label;

      const cooldown = document.createElement('div');
      // Named, because a slot is no longer one div deep: the plate, the scrim
      // and the coloured edge are divs too, and "the first div in the button"
      // stopped meaning the cooldown shade the moment art landed.
      cooldown.dataset.role = 'cooldown';
      cooldown.className = 'absolute inset-x-0 bottom-0 bg-[#0d0905]/80 text-[13px] font-bold text-[#ffeccc] '
        + 'flex items-center justify-center pointer-events-none';
      cooldown.style.height = '0%';

      // Painted like an ability, for the reason given at the top of this
      // method: these sit in the same row. There is no glyph to fall back to —
      // a consumable has no world effect and never had one — so a failed
      // fetch leaves the name and the charge count, which is the information.
      button.append(
        abilityArtElement(consumableIconUrl(consumable.id), () => {}),
        slotScrimElement(),
      );
      button.append(key, name, charges, cooldown);
      button.addEventListener('pointerdown', (e) => { e.preventDefault(); this.onActivate(consumable.slot); });
      this.slotsRow.appendChild(button);
      this.slotEls.set(consumable.slot, { button, cooldown, charges });
    }
  }

  dispose(): void {
    this.hideCard();
    this.root.remove();
    this.card.remove();
  }
}
